import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import { FaHome, FaChartLine, FaExchangeAlt, FaTwitter } from 'react-icons/fa';
import CloseIcon from '@material-ui/icons/Close';
import { uniswapLogo } from './config';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from './NodeLists';

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: '5px',
    top: '5px',
    color: 'white',
    padding: '4px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  tokenOverlay: {
    position: 'absolute', // Changed from fixed to absolute
    top: 50, // Align to the top
    left: 50, // Align to the left
    zIndex: 1300, // Keep on top
    padding: '0px',
    color: 'white', // Set the text color to white
    textAlign: 'left', 
  },
  tokenContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    color: 'white',
    margin: '0px auto',
    maxWidth: '100%',
    maxHeight: 'none',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    padding: '5px',
    fontFamily: '"Arial", sans-serif',
    lineHeight: 1.6,
    position: 'relative',
    border: '1px solid blue',
  },
  tokenContainerMobile: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    margin: '10px auto',
    maxWidth: '100%',
    maxHeight: '530px',
    padding: '0px',
    fontFamily: '"Arial", sans-serif',
    lineHeight: 1.6,
    position: 'relative',
    borderTop: '2px solid #2196f3',
  },
  tokenHeader: {
    display: 'flex',
    alignItems: 'top', // Align items vertically
    paddingBottom: '10px', // Add some padding at the bottom of the header
    color: 'white,'
  },
  tokenImage: {
    width: '32px',
    height: '32px',
    marginRight: '12px',
    verticalAlign: 'middle',
  },
  tokenInfo: {
    paddingRight: '50px',
    color: 'white',
    textAlign: 'left', // Ensure text is left-aligned
    '& h1': {
      margin: 0,
      padding: 0,
      color: '#007bff',
      textAlign: 'left', // Ensure heading is left-aligned
    },
    '& p': {
      margin: '0px 0', // Reduced vertical margin from 10px to 5px
      textAlign: 'left', // Ensure paragraphs are left-aligned
    },
    '& a': {
      color: '#007bff',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  descriptionArea: {
    color: 'white',
    maxHeight: '400px', // Set a maximum height
    overflowY: 'auto', // Enable vertical scrolling
    padding: '0px', // Optional: add some padding
    marginTop: '5px', // Optional: add some space between the token info and the description
    textAlign: 'left', // Ensure text is left-aligned
  },
  copyIcon: {
    color: 'white',
    cursor: 'pointer',
    marginLeft: '5px',
    '&:hover': {
      color: '#007bff',
    },
  },
  iconLinks: {
    display: 'flex', // Align icons in a row
    justifyContent: 'start', // Align items to the start of the container
    alignItems: 'center', // Vertically align icons in the middle
    gap: theme.spacing(2), // Create space between icons

    // If you want the icons to be vertically aligned with the text
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0),

    '& a': {
      textDecoration: 'none', // Remove underline from links
      color: 'inherit', // Icons take the color from the parent element
    },

    '& a:hover': {
      // Hover styles for the links
      opacity: 0.7, // Slight fade effect on hover
    },
  },
  linkIcon: {
    width: '20px',
    height: '20px',
    marginRight: theme.spacing(1),
    '&:hover': {
      color: theme.palette.primary.main, // Changes color on hover
    },
    // Add other styles like size, padding etc.
  },
  closeButton: {
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  rightPaneDesktop: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    marginLeft: theme.spacing(2),
    minWidth: 0,
  },
  descriptionDesktop: {
    flex: 1,
    minHeight: 0,
  },
}));

const TradingViewWidget = ({ data }) => {
  let currentSymbol = null;

  useEffect(() => {
    if (!data || !data.symbol) return;

    const appendScript = () => {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js';
      script.async = true;
      const symbol = String(data.symbol || '').toUpperCase();

      const settings = {
        symbol: symbol === 'USDT' ? symbol : `MEXC:${symbol}USDT`,
        width: 350,
        height: 220,
        locale: 'en',
        dateRange: '1D',
        colorTheme: 'dark',
        isTransparent: true,
        autosize: false,
        largeChartUrl: '',
      };

      script.innerHTML = JSON.stringify(settings);

      const container = document.querySelector('.tradingview-widget-container__widget');
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
        container.appendChild(script);
      }
    };

    const newSymbol = String(data.symbol || '').toUpperCase();
    if (!currentSymbol || currentSymbol !== newSymbol) {
      appendScript();
      currentSymbol = newSymbol;
    }
  }, [data]);

  if (!data || !data.symbol) {
    return null;
  }

  return (
    <div className="tradingview-widget-container" id="tradingview-widget-container" style={{ textAlign: 'left' }}>
      <div className="tradingview-widget-container__widget" style={{ textAlign: 'left' }}></div>
    </div>
  );
};


export default function TokenInfoComponent({ data, onClose, isMobile, isPortrait }) {
  const classes = useStyles();
  const navigate = useNavigate();

  const currentPrice = data && data.market_data && data.market_data.current_price
    ? data.market_data.current_price.usd
    : null;

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  function formatShortAddress(address) {
    if (address && address.length > 8) {
      const start = address.substring(0, 6); // '0x' + first 4 characters
      const end = address.substring(address.length - 4); // last 4 characters
      return `${start}…${end}`; // Using the ellipsis character directly
    }
    return address;
  }

  const handleOpenAgentDashboard = () => {
    try {
      const addrRaw = data && data.contract_address ? String(data.contract_address).trim() : '';
      const addr = addrRaw.toLowerCase();
      if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) return;
      if (typeof onClose === 'function') onClose();
      navigate(`/agents/${addr}`);
    } catch (e) {
    }
  };
  
  return (
    isMobile ? (
      <div style={{ height: '200px' }} className={classes.tokenOverlay}>
        <div className={classes.tokenContainerMobile}>
          <IconButton className={classes.closeButton} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        <div style={{ width: (isMobile) ? ((!isPortrait) ? '375px' : '330px' ) : '300px', marginLeft: '0px', paddingLeft: '0px' }} className={classes.tokenHeader}>
          <div style={{ width: (isMobile) ? ((!isPortrait) ? '375px' : '300px' ) : '300px', marginLeft: '0px', paddingLeft: '0px' }} className={classes.tokenInfo}>
            <h2 style={{ display: 'flex', textAlign: 'left' }}><img src={data.image.large} alt={`${data.name} Logo`} className={classes.tokenImage} />{data.name}</h2>
            {!isMobile && (
              <div>
                <p><strong>Symbol:</strong> {data.symbol.toUpperCase()}</p>
                <p>
                <strong>Contract:</strong> {formatShortAddress(data.contract_address)}
                  <Tooltip title="Copy to clipboard">
                      <IconButton
                          size="small"
                          className={classes.copyIcon}
                          onClick={() => handleCopyToClipboard(data.contract_address)}
                      >
                      <FileCopyIcon fontSize="inherit" />
                      </IconButton>
                  </Tooltip>
                </p>
                <p><strong>Current Price:</strong> {currentPrice ? formatPrice(currentPrice) : ''}</p>
                {typeof data.market_cap_rank === 'number' && (
                  <p><strong>Market Cap Rank:</strong> #{data.market_cap_rank}</p>
                )}
              </div>
            )}
            {data.market_data && data.market_data.market_cap && data.market_data.market_cap.usd && (
              <p><strong>Market Cap:</strong> ${data.market_data.market_cap.usd.toLocaleString()}</p>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenAgentDashboard}
              disabled={!data || !data.contract_address || !/^0x[a-fA-F0-9]{40}$/.test(String(data.contract_address || '').trim())}
              style={{ marginTop: 8, marginBottom: 6 }}
            >
              Agent Dashboard
            </Button>
            <div className={classes.iconLinks}>
              {data.links && data.links.homepage && (
                <Tooltip title="Go to Homepage">
                  <a href={data.links.homepage[0]} target="_blank" rel="noopener noreferrer">
                    <FaHome className={classes.linkIcon} />
                  </a>
                </Tooltip>
              )}
              {data.contract_address && (
                <Tooltip title="View Price History">
                  <a href={`https://etherscan.io/dex/uniswapv2/${data.contract_address}`} target="_blank" rel="noopener noreferrer">
                    <FaChartLine className={classes.linkIcon} />
                  </a>
                </Tooltip>
              )}
              {data.contract_address && (
                <Tooltip title="View Transactions">
                  <a href={`https://etherscan.io/address/${data.contract_address}`} target="_blank" rel="noopener noreferrer">
                    <FaExchangeAlt className={classes.linkIcon} />
                  </a>
                </Tooltip>
              )}
              {data.twitter_screen_name && (
                <Tooltip title="View on Twitter">
                  <a href={`https://twitter.com/${data.links.twitter_screen_name}`} target="_blank" rel="noopener noreferrer">
                    <FaTwitter className={classes.linkIcon} />
                  </a>
                </Tooltip>
              )}
              {data.contract_address && (
              <Tooltip title="UniSwap">
                <a href={`https://app.uniswap.org/tokens/ethereum/${data.contract_address}`} target="_blank" rel="noopener noreferrer">
                  <img src={uniswapLogo} className={classes.linkIcon} width="32" height="32" />
                </a>
              </Tooltip>
              )}
            </div>
            {(data.description && data.description.en && isMobile && isPortrait) && (
              <div style={{ width: '325px', height: '200px', backgroundColor: 'rgba(0, 0, 0, 0.80)', paddingTop: '5px', margin: '0px', textAlign: 'left' }} className={classes.descriptionArea}>
                {data.description.en}
              </div>
            )}
            {(data.description && data.description.en && isMobile && !isPortrait) && (
              <div style={{ width: '325px', height: '100px', backgroundColor: 'rgba(0, 0, 0, 0.80)', paddingTop: '5px', margin: '0px', textAlign: 'left' }} className={classes.descriptionArea}>
                {(data.description.en) ? data.description.en : ''}
              </div>
            )}
          </div>
        </div>
        {!isMobile && data.description && data.description.en && (
          <div className={classes.descriptionArea} style={{textAlign: 'left'}}>
            {(data.description.en) ? data.description.en : ''}
          </div>
        )}
        {!isMobile && (
          <TradingViewWidget data={data} />
        )}
   
        </div>
      </div>
    ) : (
      <div className={classes.tokenContainer}>
        <IconButton className={classes.closeButton} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
        {/* Top row: details on the left, chart on the right */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', width: '100%' }}>
          <div style={{ width: '320px', flex: '0 0 320px' }}>
            <div style={{ width: '100%', marginLeft: '0px', paddingLeft: '0px' }} className={classes.tokenHeader}>
              <div style={{ width: '100%', marginLeft: '0px', paddingLeft: '0px' }} className={classes.tokenInfo}>
                <h2 style={{ display: 'flex', textAlign: 'left' }}><img src={data.image.large} alt={`${data.name} Logo`} className={classes.tokenImage} />{data.name}</h2>
                <div>
                  <p><strong>Symbol:</strong> {data.symbol.toUpperCase()}</p>
                  <p>
                    <strong>Contract:</strong> {formatShortAddress(data.contract_address)}
                    <Tooltip title="Copy to clipboard">
                      <IconButton size="small" className={classes.copyIcon} onClick={() => handleCopyToClipboard(data.contract_address)}>
                        <FileCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </p>
                  <p><strong>Current Price:</strong> {currentPrice ? formatPrice(currentPrice) : ''}</p>
                  {typeof data.market_cap_rank === 'number' && (
                    <p><strong>Market Cap Rank:</strong> #{data.market_cap_rank}</p>
                  )}
                </div>
                {data.market_data && data.market_data.market_cap && data.market_data.market_cap.usd && (
                  <p><strong>Market Cap:</strong> ${data.market_data.market_cap.usd.toLocaleString()}</p>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenAgentDashboard}
                  disabled={!data || !data.contract_address || !/^0x[a-fA-F0-9]{40}$/.test(String(data.contract_address || '').trim())}
                  style={{ marginTop: 8, marginBottom: 6 }}
                >
                  Agent Dashboard
                </Button>
                <div className={classes.iconLinks}>
                  {data.links && data.links.homepage && (
                    <Tooltip title="Go to Homepage">
                      <a href={data.links.homepage[0]} target="_blank" rel="noopener noreferrer">
                        <FaHome className={classes.linkIcon} />
                      </a>
                    </Tooltip>
                  )}
                  {data.contract_address && (
                    <Tooltip title="View Price History">
                      <a href={`https://etherscan.io/dex/uniswapv2/${data.contract_address}`} target="_blank" rel="noopener noreferrer">
                        <FaChartLine className={classes.linkIcon} />
                      </a>
                    </Tooltip>
                  )}
                  {data.contract_address && (
                    <Tooltip title="View Transactions">
                      <a href={`https://etherscan.io/address/${data.contract_address}`} target="_blank" rel="noopener noreferrer">
                        <FaExchangeAlt className={classes.linkIcon} />
                      </a>
                    </Tooltip>
                  )}
                  {data.twitter_screen_name && (
                    <Tooltip title="View on Twitter">
                      <a href={`https://twitter.com/${data.links.twitter_screen_name}`} target="_blank" rel="noopener noreferrer">
                        <FaTwitter className={classes.linkIcon} />
                      </a>
                    </Tooltip>
                  )}
                  {data.contract_address && (
                    <Tooltip title="UniSwap">
                      <a href={`https://app.uniswap.org/tokens/ethereum/${data.contract_address}`} target="_blank" rel="noopener noreferrer">
                        <img src={uniswapLogo} className={classes.linkIcon} width="32" height="32" />
                      </a>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 260 }}>
            <TradingViewWidget data={data} />
          </div>
        </div>

        {/* Bottom row: full-width description spanning both columns */}
        {data.description && data.description.en && (
          <div className={classes.descriptionArea} style={{ textAlign: 'left', width: '100%' }}>
            {data.description.en}
          </div>
        )}
      </div>
    )
  );
}
